import { Button } from "@/components/ui/Button";
import { PageNavigation } from "@/features/collections/CollectionControls";
import { PlatformQueryNotice } from "../components/PlatformQueryNotice";
import { useEffect, useMemo, useState } from "react";
import {
  Archive,
  Database,
  FileText,
  FileVideo,
  FolderTree,
  HardDrive,
  Image,
  Layers,
  RefreshCw,
  Search,
  Server,
  Video,
} from "lucide-react";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { cn } from "@/lib/cn";
import type {
  AdminFileInventoryItem,
  AdminFileKind,
  AdminFileInventoryRoot,
} from "@/lib/types";
import { AdminIconButton } from "@/components/console/AdminIconButton";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import { useOperatorFiles } from "../hooks/useOperatorData";

const KIND_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All files" },
  { value: "raw_video", label: "Raw uploads" },
  { value: "mp4", label: "Finished MP4" },
  { value: "hls_manifest", label: "HLS manifests" },
  { value: "hls_segment", label: "HLS segments" },
  { value: "thumbnail", label: "Thumbnails" },
  { value: "logo", label: "Logos" },
  { value: "asset", label: "Assets" },
  { value: "other", label: "Other" },
];

const ROOT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All roots" },
  { value: "hot", label: "Hot media" },
  { value: "cold", label: "Original uploads" },
  { value: "assets", label: "App assets" },
];

const KIND_LABEL: Record<AdminFileKind, string> = {
  raw_video: "Raw upload",
  mp4: "Finished MP4",
  hls_manifest: "HLS manifest",
  hls_segment: "HLS segment",
  thumbnail: "Thumbnail",
  logo: "Logo",
  asset: "Asset",
  other: "Other",
};

const KIND_TONE: Record<AdminFileKind, string> = {
  raw_video: "border-accent/40 bg-accent/10 text-accent-text",
  mp4: "border-good/40 bg-good/10 text-good-text",
  hls_manifest: "border-rule bg-elevated text-ink",
  hls_segment: "border-rule bg-field text-ink-muted",
  thumbnail: "border-accent/30 bg-accent/10 text-accent-text",
  logo: "border-good/30 bg-good/10 text-good-text",
  asset: "border-rule bg-elevated text-ink-muted",
  other: "border-rule bg-elevated text-ink-muted",
};

/**
 * Operator storage inventory (admin-console-split PR 4) — the platform-wide
 * port of the club-era AdminFilesPage, fed by /api/super-admin/files. The
 * club console drops its Media Files section at cutover; this page is the
 * operator home for media roots, space hogs, and cleanup review.
 */
export function StoragePage() {
  const query = useOperatorFiles();
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("all");
  const [root, setRoot] = useState("all");
  const payload = query.data;

  const rootOptions = useMemo(() => {
    const roots = new Set((payload?.roots ?? []).map((r) => r.id));
    return ROOT_OPTIONS.filter(
      (opt) =>
        opt.value === "all" ||
        roots.has(opt.value as AdminFileInventoryRoot["id"]),
    );
  }, [payload?.roots]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (payload?.files ?? []).filter((file) => {
      if (kind !== "all" && file.kind !== kind) return false;
      if (root !== "all" && file.root !== root) return false;
      if (!term) return true;
      const haystack = [
        file.relative_path,
        file.match_id,
        file.match_label,
        file.team_id,
        file.slot,
        file.root_label,
        KIND_LABEL[file.kind],
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [kind, payload?.files, root, search]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  useEffect(() => setPage(1), [search, kind, root, pageSize]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pages);
  const pageRows = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );
  const kindCounts = payload?.totals.by_kind ?? {};
  const kindBytes = payload?.totals.by_kind_bytes ?? {};
  const roots = payload?.roots ?? [];
  const largestMedia = useMemo(
    () =>
      (payload?.files ?? [])
        .filter((file) =>
          ["raw_video", "mp4", "hls_segment", "hls_manifest"].includes(
            file.kind,
          ),
        )
        .slice()
        .sort((a, b) => b.bytes - a.bytes)
        .slice(0, 5),
    [payload?.files],
  );
  const cleanupCandidates = useMemo(
    () =>
      (payload?.files ?? [])
        .filter((file) => file.referenced === false)
        .slice()
        .sort((a, b) => b.bytes - a.bytes)
        .slice(0, 5),
    [payload?.files],
  );

  return (
    <div data-testid="admin-files" className="flex flex-col gap-4">
      <AdminPageHeader
        eyebrow="Operations"
        title="Media Storage"
        description="Where video assets live across every club, what consumes space, and which files deserve operator review."
        meta={
          query.isFetching
            ? "Refreshing..."
            : `Updated ${formatGeneratedAt(payload?.generated_at)}`
        }
        actions={
          <AdminIconButton
            visibleLabel
            icon={RefreshCw}
            label="Refresh files"
            size="md"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          />
        }
      />

      {query.error ? (
        <p
          data-testid="admin-files-error"
          className="text-xs font-mono text-destructive-text"
        >
          Failed to load file inventory.
        </p>
      ) : null}

      <PlatformQueryNotice
        pending={query.isPending}
        error={query.error}
        label="media inventory"
        retry={query.refetch}
      />
      {payload ? (
        <>
          <StorageHealthStrip
            roots={roots}
            totalBytes={payload?.totals.bytes ?? 0}
            fileCount={payload?.totals.file_count ?? 0}
            truncated={payload?.truncated ?? false}
          />

          <section className="grid gap-3 md:grid-cols-3">
            {roots.map((storageRoot) => (
              <StorageRootTile key={storageRoot.id} root={storageRoot} />
            ))}
          </section>

          <section className="platform-kpi-grid">
            <MetricTile
              icon={Archive}
              label="Raw uploads"
              value={String(kindCounts.raw_video ?? 0)}
              sub={formatBytes(kindBytes.raw_video ?? 0)}
            />
            <MetricTile
              icon={Video}
              label="Playable video"
              value={String(
                (kindCounts.mp4 ?? 0) + (kindCounts.hls_manifest ?? 0),
              )}
              sub={`${kindCounts.hls_segment ?? 0} HLS segments`}
            />
            <MetricTile
              icon={Image}
              label="Images"
              value={String(
                (kindCounts.thumbnail ?? 0) + (kindCounts.logo ?? 0),
              )}
              sub="Thumbnails, logos, favicons"
            />
            <MetricTile
              icon={Database}
              label="Scanned"
              value={payload ? String(payload.totals.file_count) : "0"}
              sub={
                payload?.truncated
                  ? "File list capped"
                  : formatBytes(payload?.totals.bytes ?? 0)
              }
              tone={payload?.truncated ? "flag" : "neutral"}
            />
          </section>

          <section className="platform-two-col">
            <StorageFocusList
              title="Largest Media"
              detail="Biggest video assets in the scanned inventory."
              files={largestMedia}
              emptyCopy="No media files were found."
            />
            <StorageFocusList
              title="Cleanup Review"
              detail="Unreferenced files that may need operator review before deletion."
              files={cleanupCandidates}
              emptyCopy="No unreferenced files found in the scanned set."
            />
          </section>

          <section className="platform-panel">
            <header className="border-b border-rule px-3 py-2">
              <h3 className="font-display text-sm uppercase tracking-wider text-ink">
                File Inventory
              </h3>
              <p className="text-xs text-ink-muted">
                Searchable forensic detail across media roots.
              </p>
            </header>
            <div className="collection-toolbar">
              <label className="collection-search">
                <Search
                  className="collection-search-icon"
                  size={16}
                  aria-hidden="true"
                />
                <span>Quick search</span>
                <input
                  aria-label="Search files"
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search path, match, team, slot..."
                  className="h-9 w-full rounded-control border border-rule bg-field pl-9 pr-3 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                />
              </label>
              <label className="collection-filter">
                <span>Kind</span>{" "}
                <TkticSelect
                  value={kind}
                  onChange={setKind}
                  options={KIND_OPTIONS}
                  ariaLabel="Filter by file type"
                  size="sm"
                />
              </label>
              <label className="collection-filter">
                <span>Root</span>{" "}
                <TkticSelect
                  value={root}
                  onChange={setRoot}
                  options={rootOptions}
                  ariaLabel="Filter by storage root"
                  size="sm"
                />
              </label>
              <Button
                variant="ghost"
                disabled={!search && kind === "all" && root === "all"}
                onClick={() => {
                  setSearch("");
                  setKind("all");
                  setRoot("all");
                }}
              >
                Clear filters
              </Button>
            </div>

            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-rule text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 font-medium">File</th>
                    <th className="px-3 py-2 font-medium">Match</th>
                    <th className="px-3 py-2 font-medium">Size</th>
                    <th className="px-3 py-2 font-medium">Modified</th>
                    <th className="px-3 py-2 font-medium">Root</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rule">
                  {pageRows.map((file) => (
                    <FileTableRow key={file.id} file={file} />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-2 p-3 md:hidden">
              {pageRows.map((file) => (
                <FileMobileRow key={file.id} file={file} />
              ))}
            </div>

            {filtered.length === 0 ? (
              <p className="p-4 text-sm text-ink-muted">
                No files match the current filters.
              </p>
            ) : null}
            <div className="collection-pagination">
              <span>
                {filtered.length ? (currentPage - 1) * pageSize + 1 : 0}–
                {Math.min(currentPage * pageSize, filtered.length)} of{" "}
                {filtered.length} files in the loaded inventory
                {payload?.truncated ? " (capped)" : ""}
              </span>
              <div className="flex items-center gap-2">
                <span>Per page</span>
                <TkticSelect
                  value={String(pageSize)}
                  options={[10, 25, 50].map((v) => ({
                    value: String(v),
                    label: String(v),
                  }))}
                  onChange={(v) => setPageSize(Number(v))}
                  ariaLabel="Files per page"
                />
                <PageNavigation
                  page={currentPage}
                  pages={pages}
                  onChange={setPage}
                />
              </div>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

function StorageHealthStrip({
  roots,
  totalBytes,
  fileCount,
  truncated,
}: {
  roots: AdminFileInventoryRoot[];
  totalBytes: number;
  fileCount: number;
  truncated: boolean;
}) {
  const missingRoots = roots.filter((root) => !root.exists).length;
  const hot = roots.find((root) => root.id === "hot");
  const cold = roots.find((root) => root.id === "cold");
  const status =
    missingRoots > 0
      ? "Needs attention"
      : truncated
        ? "Inventory capped"
        : "Online";
  const tone = missingRoots > 0 ? "bad" : truncated ? "warn" : "good";

  return (
    <section
      data-testid="admin-storage-status"
      data-status={status.toLowerCase().replace(" ", "-")}
      className="platform-panel"
    >
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <StorageDot tone={tone} />
            <p className="font-display text-sm uppercase tracking-wider text-ink">
              {status}
            </p>
            <span className="rounded-pill border border-rule px-2 py-0.5 font-mono text-[0.65rem] text-ink-muted">
              {fileCount} files scanned
            </span>
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            {missingRoots > 0
              ? "One or more configured media roots is missing from the filesystem."
              : truncated
                ? "The inventory reached its detail cap; totals are still useful, but the table is partial."
                : "All configured media roots are reachable in the current scan."}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:flex lg:items-center">
          <StorageSignal
            label="Total"
            value={formatBytes(totalBytes)}
            tone="muted"
          />
          <StorageSignal
            label="Hot media"
            value={formatBytes(hot?.bytes ?? 0)}
            tone={hot?.exists === false ? "bad" : "muted"}
          />
          <StorageSignal
            label="Originals"
            value={formatBytes(cold?.bytes ?? 0)}
            tone={cold?.exists === false ? "bad" : "muted"}
          />
          <StorageSignal
            label="Missing roots"
            value={String(missingRoots)}
            tone={missingRoots > 0 ? "bad" : "good"}
          />
        </div>
      </div>
    </section>
  );
}

function StorageFocusList({
  title,
  detail,
  files,
  emptyCopy,
}: {
  title: string;
  detail: string;
  files: AdminFileInventoryItem[];
  emptyCopy: string;
}) {
  return (
    <section className="platform-panel">
      <header className="flex items-baseline justify-between gap-2">
        <div>
          <h3 className="font-display text-sm uppercase tracking-wider text-ink">
            {title}
          </h3>
          <p className="text-xs text-ink-muted">{detail}</p>
        </div>
        <span className="font-mono text-[0.65rem] text-ink-muted">
          {files.length}
        </span>
      </header>
      {files.length === 0 ? (
        <p className="mt-3 text-xs text-ink-muted">{emptyCopy}</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {files.map((file) => (
            <li
              key={file.id}
              className="grid grid-cols-[1fr_auto] gap-2 rounded-inset border border-rule/60 bg-elevated px-2 py-1.5"
            >
              <div className="min-w-0">
                <p className="truncate font-mono text-xs text-ink">
                  {file.match_label ?? file.relative_path}
                </p>
                <p className="truncate font-mono text-[0.6rem] text-ink-muted">
                  {KIND_LABEL[file.kind]} · {file.root_label}
                  {file.slot ? ` · ${file.slot}` : ""}
                </p>
              </div>
              <span className="font-mono text-xs text-ink-muted">
                {formatBytes(file.bytes)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StorageRootTile({ root }: { root: AdminFileInventoryRoot }) {
  const Icon =
    root.id === "assets" ? FolderTree : root.id === "cold" ? HardDrive : Server;
  return (
    <article className="platform-panel">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-inset bg-elevated text-ink">
          <Icon size={18} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-display uppercase tracking-wider text-ink">
              {root.label}
            </h3>
            <span
              className={cn(
                "rounded-pill border px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider",
                root.exists
                  ? "border-good/40 bg-good/10 text-good-text"
                  : "border-destructive/40 bg-destructive/10 text-destructive-text",
              )}
            >
              {root.exists ? "Online" : "Missing"}
            </span>
          </div>
          <p className="mt-1 break-all font-mono text-[0.65rem] text-ink-muted">
            {root.path}
          </p>
          <p className="mt-2 text-xs font-mono text-ink-muted">
            {root.file_count} files · {formatBytes(root.bytes)}
          </p>
        </div>
      </div>
    </article>
  );
}

function MetricTile({
  icon: Icon,
  label,
  value,
  sub,
  tone = "neutral",
}: {
  icon: typeof Database;
  label: string;
  value: string;
  sub: string;
  tone?: "neutral" | "flag";
}) {
  return (
    <article className="platform-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            {label}
          </p>
          <p
            className={cn(
              "mt-1 font-mono text-2xl",
              tone === "flag" ? "text-accent-text" : "text-ink",
            )}
          >
            {value}
          </p>
        </div>
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-inset bg-elevated text-ink-muted">
          <Icon size={18} aria-hidden="true" />
        </span>
      </div>
      <p className="mt-2 text-xs font-mono text-ink-muted">{sub}</p>
    </article>
  );
}

function FileTableRow({ file }: { file: AdminFileInventoryItem }) {
  return (
    <tr className="align-top hover:bg-field/70">
      <td className="px-3 py-2">
        <KindPill kind={file.kind} />
      </td>
      <td className="max-w-[28rem] px-3 py-2">
        <div className="flex min-w-0 items-start gap-2">
          <FileKindIcon kind={file.kind} />
          <div className="min-w-0">
            <p className="break-all font-mono text-xs text-ink">
              {file.relative_path}
            </p>
            {file.slot ? (
              <p className="mt-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Slot {file.slot}
              </p>
            ) : null}
          </div>
        </div>
      </td>
      <td className="px-3 py-2 text-xs text-ink-muted">
        {file.match_label || file.match_id ? (
          <div>
            <p className="max-w-[16rem] truncate text-ink">
              {file.match_label ?? file.match_id}
            </p>
            <p className="font-mono text-[0.65rem]">
              {file.match_id}
              {file.team_id ? ` · ${file.team_id}` : ""}
            </p>
          </div>
        ) : (
          <span>—</span>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-ink-muted">
        {formatBytes(file.bytes)}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-ink-muted">
        {formatDateTime(file.modified_at)}
      </td>
      <td className="px-3 py-2 text-xs text-ink-muted">{file.root_label}</td>
    </tr>
  );
}

function FileMobileRow({ file }: { file: AdminFileInventoryItem }) {
  return (
    <article className="rounded-inset border border-rule bg-elevated p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <KindPill kind={file.kind} />
          <p className="mt-2 break-all font-mono text-xs text-ink">
            {file.relative_path}
          </p>
        </div>
        <FileKindIcon kind={file.kind} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-ink-muted">
        <p className="font-mono">{formatBytes(file.bytes)}</p>
        <p className="font-mono text-right">
          {formatDateTime(file.modified_at)}
        </p>
        <p>{file.root_label}</p>
        <p className="truncate text-right">
          {file.match_label ?? file.match_id ?? "Unlinked"}
        </p>
      </div>
    </article>
  );
}

function KindPill({ kind }: { kind: AdminFileKind }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-pill border px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider",
        KIND_TONE[kind],
      )}
    >
      {KIND_LABEL[kind]}
    </span>
  );
}

function FileKindIcon({ kind }: { kind: AdminFileKind }) {
  const Icon =
    kind === "raw_video" || kind === "mp4"
      ? FileVideo
      : kind === "hls_manifest" || kind === "hls_segment"
        ? Layers
        : kind === "thumbnail" || kind === "logo"
          ? Image
          : FileText;
  return (
    <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-inset bg-elevated text-ink-muted">
      <Icon size={14} aria-hidden="true" />
    </span>
  );
}

type StorageTone = "good" | "warn" | "bad" | "muted";

function StorageSignal({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: StorageTone;
}) {
  const classes: Record<StorageTone, string> = {
    good: "text-good-text",
    warn: "text-accent-text",
    bad: "text-destructive-text",
    muted: "text-ink",
  };
  return (
    <div className="rounded-inset border border-rule bg-elevated px-2 py-1.5">
      <span className="block text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className={`font-mono text-xs ${classes[tone]}`}>{value}</span>
    </div>
  );
}

function StorageDot({ tone }: { tone: StorageTone }) {
  const classes: Record<StorageTone, string> = {
    good: "bg-good",
    warn: "bg-accent",
    bad: "bg-destructive",
    muted: "bg-ink-muted",
  };
  return (
    <span
      aria-hidden="true"
      className={`mt-1 h-2 w-2 shrink-0 rounded-full ${classes[tone]}`}
    />
  );
}

function formatBytes(bytes: number | undefined | null): string {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDateTime(value: string | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatGeneratedAt(value: string | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
